import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getActiveJobs } from '../services/jobService';
import { applyForJob } from '../services/applicationService';
// import { checkPackageLimit } from '../services/subscriptionService';
import { getProfileStats } from '../services/seekerProfileService';
import JobCard from '../components/JobCard';
import JobApplicationModal from '../components/JobApplicationModal';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

const Jobs = ({ user, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [filters, setFilters] = useState({
    jobType: '',
    workMode: '',
    experienceLevel: '',
    location: '',
    search: location.state?.search || ''
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, filters]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      console.log('Fetching all active jobs...');
      const result = await getActiveJobs({}, { limit: 100 });
      console.log('Jobs result:', result);
      if (result.success) {
        setJobs(result.jobs);
      } else {
        console.error('Failed to fetch jobs:', result.error);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    // Filter by job type
    if (filters.jobType) {
      filtered = filtered.filter(job => job.jobType === filters.jobType);
    }

    // Filter by work mode
    if (filters.workMode) {
      filtered = filtered.filter(job => job.workMode === filters.workMode);
    }

    // Filter by experience level
    if (filters.experienceLevel) {
      filtered = filtered.filter(job => job.experienceLevel === filters.experienceLevel);
    }

    // Filter by location
    if (filters.location) {
      filtered = filtered.filter(job =>
        job.location.city.toLowerCase().includes(filters.location.toLowerCase()) ||
        job.location.state.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Filter by search term
    if (filters.search) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.company.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.requiredSkills.some(skill =>
          skill.toLowerCase().includes(filters.search.toLowerCase())
        )
      );
    }

    setFilteredJobs(filtered);
  };

  const handleApplyClick = async (job, setJobApplying, setJobApplied) => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Subscription checks disabled temporarily to allow free applications for job seekers.
    // try {
    //   const limitCheck = await checkPackageLimit(user.uid, 'application');
    //   
    //   if (!limitCheck.success) {
    //     if (limitCheck.needsSubscription) {
    //       const confirmPurchase = window.confirm(
    //         '❌ No Active Subscription\n\n' +
    //         'You need an active subscription to apply for jobs.\n\n' +
    //         'Would you like to view our packages?'
    //       );
    //       if (confirmPurchase) {
    //         navigate('/packages');
    //       }
    //       return;
    //     } else if (limitCheck.limitReached) {
    //       const confirmUpgrade = window.confirm(
    //         '❌ Application Limit Reached\n\n' +
    //         `You have used all ${limitCheck.max} applications in your current package.\n\n` +
    //         'Would you like to upgrade your package?'
    //       );
    //       if (confirmUpgrade) {
    //         navigate('/packages');
    //       }
    //       return;
    //     }
    //   }
    // } catch (error) {
    //   console.error('Error checking subscription:', error);
    //   alert('Failed to verify subscription. Please try again.');
    //   return;
    // }

    // Check if profile is complete before showing modal
    try {
      const profileStats = await getProfileStats(user.uid);
      if (profileStats.success && profileStats.stats.completionPercentage < 85) {
        if (window.confirm('Your profile is not complete. You need at least 85% completion to apply for jobs. Would you like to complete your profile now?')) {
          navigate('/seeker-profile');
        }
        return;
      }
    } catch (error) {
      console.error('Error checking profile completion:', error);
    }

    // All checks passed, show the application modal
    setSelectedJob(job);
    setShowJobApplicationModal(true);
  };

  const handleConfirmApplication = async (applicationData) => {
    if (!selectedJob) return;

    setApplying(true);
    try {
      const result = await applyForJob(selectedJob.id, user.uid, applicationData);

      if (result.success) {
        setShowJobApplicationModal(false);
        setSelectedJob(null);
        setShowSuccessAnimation(true);

        // Hide success animation after 3 seconds
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 3000);

        // Refresh jobs to update applied status
        fetchJobs();
      } else {
        alert(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      jobType: '',
      workMode: '',
      experienceLevel: '',
      location: '',
      search: ''
    });
  };
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Hero Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-72 h-72 bg-white opacity-10 rounded-full -translate-x-36 -translate-y-36"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-48 -translate-y-48"></div>
            <div className="absolute bottom-0 left-1/2 w-80 h-80 bg-white opacity-10 rounded-full translate-x-40 translate-y-40"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Browse All Jobs
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Discover your next career opportunity from our latest job postings
              </p>
              <div className="flex items-center justify-center gap-2 text-blue-100">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">{jobs.length} Active Opportunities</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filters Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Find Your Perfect Job</h2>
            </div>

            <div className="space-y-6">
              {/* Search Bar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Jobs
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search by title, company, skills, or keywords..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Filter Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Job Type</label>
                  <select
                    value={filters.jobType}
                    onChange={(e) => handleFilterChange('jobType', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">All Job Types</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Work Mode</label>
                  <select
                    value={filters.workMode}
                    onChange={(e) => handleFilterChange('workMode', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">All Work Modes</option>
                    <option value="Work From Home">Work From Home</option>
                    <option value="In Field">In Field</option>
                    <option value="Work From Office">Work From Office</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Experience Level</label>
                  <select
                    value={filters.experienceLevel}
                    onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">All Experience Levels</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Location</label>
                  <input
                    type="text"
                    placeholder="City or State"
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Results Count and Clear Filters */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    Showing {filteredJobs.length} of {jobs.length} jobs
                  </span>
                  {filteredJobs.length !== jobs.length && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      Filtered
                    </span>
                  )}
                </div>
                {(filters.search || filters.jobType || filters.workMode || filters.experienceLevel || filters.location) && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Jobs List */}
          {filteredJobs.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {jobs.length === 0 ? 'No Jobs Available' : 'No Jobs Match Your Filters'}
              </h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {jobs.length === 0
                  ? 'Check back later for new job opportunities. We add new positions regularly!'
                  : 'Try adjusting your search criteria or clearing some filters to see more results.'
                }
              </p>
              {jobs.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  showActions={false}
                  user={user}
                  userData={userData}
                  onApplyClick={handleApplyClick}
                  onClick={() => {
                    navigate(`/jobs/${job.id}`);
                  }}
                />
              ))}
            </div>
          )}

          {/* Load More (if needed) */}
          {filteredJobs.length > 0 && filteredJobs.length === jobs.length && jobs.length >= 100 && (
            <div className="text-center mt-8">
              <button
                onClick={() => {
                  // Implement load more functionality
                  console.log('Load more jobs');
                }}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Load More Jobs
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Success Animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform transition-all duration-300 scale-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Application Submitted!</h3>
            <p className="text-gray-600 mb-4">Your application has been sent successfully. Good luck!</p>
            <div className="flex items-center justify-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Job Application Modal */}
      <JobApplicationModal
        job={selectedJob}
        isOpen={showJobApplicationModal}
        onClose={() => {
          setShowJobApplicationModal(false);
          setSelectedJob(null);
        }}
        onConfirm={handleConfirmApplication}
        applying={applying}
      />

      {/* Footer */}
      <Footer />
    </>
  );
};

export default Jobs;
